import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Divider,
  Card,
  CardContent,
  Tooltip,
  IconButton,
  Grid
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoIcon from '@mui/icons-material/Info';

const ScopeSection = ({ projectId, data, onChange }) => {
  // Local state to track all form fields
  const [formData, setFormData] = useState({
    projectObjectives: data.projectObjectives || '',
    businessGoals: data.businessGoals || '',
    projectScope: data.projectScope || '',
    keyFeatures: data.keyFeatures || '',
    outOfScope: data.outOfScope || '',
    success: {
      userAcquisition: data.success?.userAcquisition || false,
      userEngagement: data.success?.userEngagement || false,
      revenue: data.success?.revenue || false,
      marketPosition: data.success?.marketPosition || false,
      productQuality: data.success?.productQuality || false,
      operationalEfficiency: data.success?.operationalEfficiency || false,
      other: data.success?.other || false
    },
    timeframe: data.timeframe || 'medium',
    constraints: data.constraints || '',
    assumptions: data.assumptions || ''
  });
  
  // Update parent component when local state changes
  // Using debounce to prevent too many updates
  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      onChange(formData);
    }, 500); // Debounce for 500ms
    
    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [formData, onChange]);
  
  // Handle text field changes
  const handleTextChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };
  
  // Handle checkbox changes for success criteria
  const handleCheckboxChange = (field) => (event) => {
    setFormData({
      ...formData,
      success: {
        ...formData.success,
        [field]: event.target.checked
      }
    });
  };
  
  // Handle radio button changes for timeframe
  const handleRadioChange = (event) => {
    setFormData({
      ...formData,
      timeframe: event.target.value
    });
  };
  
  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Project Scope Definition
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Define the boundaries and goals of your project. This section helps establish what the project will and won't do.
      </Typography>
      
      <Divider sx={{ my: 3 }} />
      
      {/* Project Objectives */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Project Objectives</Typography>
          <Tooltip title="What is this project trying to achieve? What problems is it solving?">
            <IconButton size="small" sx={{ ml: 1 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          className="multi-line-field"
          multiline
          rows={4}
          placeholder="Describe the main objectives of your project. What are you trying to accomplish?"
          value={formData.projectObjectives}
          onChange={handleTextChange('projectObjectives')}
        />
      </Box>
      
      {/* Business Goals */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Business Goals</Typography>
          <Tooltip title="How does this project align with business objectives? What business outcomes do you expect?">
            <IconButton size="small" sx={{ ml: 1 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          className="multi-line-field"
          multiline
          rows={3}
          placeholder="How does this project align with your organization's business goals?"
          value={formData.businessGoals}
          onChange={handleTextChange('businessGoals')}
        />
      </Box>
      
      {/* Project Scope */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Project Scope</Typography>
          <Tooltip title="What's included in this project? Define the boundaries clearly.">
            <IconButton size="small" sx={{ ml: 1 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          className="multi-line-field"
          multiline
          rows={4}
          placeholder="Define what is included in the scope of this project."
          value={formData.projectScope}
          onChange={handleTextChange('projectScope')}
        />
      </Box>
      
      {/* Key Features */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Key Features</Typography>
          <Tooltip title="List the most important features that will be implemented.">
            <IconButton size="small" sx={{ ml: 1 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          className="multi-line-field"
          multiline
          rows={4}
          placeholder="List the key features and functionality that will be included in the project."
          value={formData.keyFeatures}
          onChange={handleTextChange('keyFeatures')}
        />
      </Box>
      
      {/* Out of Scope */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Out of Scope</Typography>
          <Tooltip title="Define what will NOT be included in this project.">
            <IconButton size="small" sx={{ ml: 1 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          className="multi-line-field"
          multiline
          rows={3}
          placeholder="Specify anything that is explicitly not included in this project."
          value={formData.outOfScope}
          onChange={handleTextChange('outOfScope')}
        />
      </Box>
      
      {/* Success Criteria */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Success Criteria</Typography>
          <Tooltip title="How will you measure if this project is successful?">
            <IconButton size="small" sx={{ ml: 1 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select all metrics that will be used to measure success:
            </Typography>
            
            <FormControl component="fieldset">
              <FormGroup>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={formData.success.userAcquisition}
                          onChange={handleCheckboxChange('userAcquisition')}
                        />
                      }
                      label="User Acquisition/Growth"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={formData.success.userEngagement}
                          onChange={handleCheckboxChange('userEngagement')}
                        />
                      }
                      label="User Engagement/Retention"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={formData.success.revenue}
                          onChange={handleCheckboxChange('revenue')}
                        />
                      }
                      label="Revenue/Cost Reduction"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={formData.success.marketPosition}
                          onChange={handleCheckboxChange('marketPosition')}
                        />
                      }
                      label="Market Position/Competitiveness"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={formData.success.productQuality}
                          onChange={handleCheckboxChange('productQuality')}
                        />
                      }
                      label="Product Quality/User Satisfaction"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={formData.success.operationalEfficiency}
                          onChange={handleCheckboxChange('operationalEfficiency')}
                        />
                      }
                      label="Operational Efficiency"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={formData.success.other}
                          onChange={handleCheckboxChange('other')}
                        />
                      }
                      label="Other"
                    />
                  </Grid>
                </Grid>
              </FormGroup>
            </FormControl>
          </CardContent>
        </Card>
      </Box>
      
      {/* Additional fields like Project Timeframe, Constraints, and Assumptions would follow */}
    </Box>
  );
};

export default ScopeSection;